import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInstructors } from '@/hooks/useInstructors'
import { useInstructorInvitations } from '@/hooks/useInstructorInvitations'
import type { EmployeeRoleCategory } from '@/hooks/useEmployeeInvitation'
import { useSchoolAffiliationRequests } from '@/hooks/useSchoolAffiliationRequests'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'
import { EmployeeOnboardingWizard } from '@/components/school/EmployeeOnboardingWizard'
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown'
import { effectiveInviteStatus } from '@/lib/invitationStatus'

type RosterStatus = 'full_time' | 'part_time' | 'casual' | 'contractor' | 'pending'
type RosterRoleCategory = EmployeeRoleCategory

interface RosterRow {
  key: string
  kind: 'active' | 'pending_invite' | 'pending_affiliation'
  fullName: string | null
  email: string | null
  jobTitle: string | null
  roleCategory: RosterRoleCategory | null
  status: RosterStatus | null
  hourlyRate: number | null
  phone: string | null
  vehicle: string | null
  vehiclePlate: string | null
  dateLabel: string
  dateIso: string
  membershipId?: string
  invitationId?: string
  affiliationId?: string
}

// 'other' (role category) and 'casual' (employment type) remain valid in the database
// (migrations 087/088) but are no longer offered in the UI, per the product owner's own
// choice — not worth the schema churn of recreating the enum type to remove them outright.
const ROLE_CATEGORY_OPTIONS: { key: RosterRoleCategory; label: string }[] = [
  { key: 'instructor', label: 'Instructor' },
  { key: 'administrator', label: 'Administrator' },
  { key: 'receptionist', label: 'Receptionist' },
  { key: 'finance', label: 'Finance' },
]

const STATUS_OPTIONS: { key: RosterStatus; label: string }[] = [
  { key: 'full_time', label: 'Full-time' },
  { key: 'part_time', label: 'Part-time' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'pending', label: 'Pending' },
]

const STATUS_STYLE: Record<RosterStatus, { bg: string; color: string; label: string }> = {
  full_time:  { bg: 'rgba(15,124,108,0.1)',  color: '#0F7C6C', label: 'Full-time' },
  part_time:  { bg: 'rgba(15,124,108,0.1)',  color: '#0F7C6C', label: 'Part-time' },
  casual:     { bg: 'rgba(15,124,108,0.1)',  color: '#0F7C6C', label: 'Casual' },
  contractor: { bg: 'rgba(27,42,74,0.07)',   color: '#1B2A4A', label: 'Contractor' },
  pending:    { bg: 'rgba(212,151,43,0.12)', color: '#9A6C0E', label: 'Pending' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

const AFFILIATION_RESPOND_ERROR_MESSAGES: Record<string, string> = {
  not_school_admin:     'You need to be an active owner or admin of this school to respond to this request.',
  not_pending:          'This request is no longer pending — it may have just been decided.',
  hourly_rate_required: 'Enter a valid hourly rate (must be greater than 0) to approve a contractor.',
  already_affiliated:   'This instructor is already affiliated with this school.',
  internal_error:       'Something went wrong. Please try again.',
}

export default function Instructors() {
  const navigate = useNavigate()
  const { instructors, loading, error, refresh } = useInstructors()
  const {
    invitations, loading: invitesLoading, error: invitesError,
    revokingId, revokeInvitation, resending, resendInvitations, refresh: refreshInvites,
  } = useInstructorInvitations()
  const {
    requests: affiliationRequests, loading: affiliationLoading, error: affiliationError,
    respondingId, respond: respondToAffiliation, refresh: refreshAffiliationRequests,
  } = useSchoolAffiliationRequests()
  const { schools, loading: schoolsLoading, selectedSchoolId } = useSelectedSchool()

  const [wizardOpen, setWizardOpen] = useState(false)
  const [formNotice, setFormNotice] = useState('')
  const [revokeError, setRevokeError] = useState('')
  const [respondError, setRespondError] = useState('')
  const [rateByRequestId, setRateByRequestId] = useState<Record<string, string>>({})
  const [roleFilter, setRoleFilter] = useState<Set<RosterRoleCategory>>(new Set())
  const [statusFilter, setStatusFilter] = useState<Set<RosterStatus>>(new Set())
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [resendNotice, setResendNotice] = useState('')

  const handleWizardSent = ({ emailed }: { emailed: boolean }) => {
    refreshInvites()
    if (emailed) {
      setFormNotice('Invitation sent.')
      // Close the modal so the admin immediately sees the new Pending row in the roster —
      // only on a clean send; when the email failed, the wizard itself stays up showing the
      // copy-link fallback until the admin clicks Done.
      setWizardOpen(false)
    }
  }

  const handleRevoke = async (id: string) => {
    setRevokeError('')
    const result = await revokeInvitation(id)
    if (!result.ok) {
      setRevokeError(
        result.code === 'not_pending'
          ? 'This invitation is no longer pending — it may have just been accepted or already revoked.'
          : 'Could not revoke this invitation. Please try again.'
      )
    } else {
      setSelected(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  const handleResendSelected = async () => {
    setResendNotice('')
    const ids = Array.from(selected)
    const result = await resendInvitations(ids)
    setSelected(new Set())
    setResendNotice(
      result.failed === 0
        ? `Resent ${result.succeeded} invitation${result.succeeded !== 1 ? 's' : ''}.`
        : `Resent ${result.succeeded}, ${result.failed} failed. Please try the failed ones again.`
    )
  }

  const handleResendOne = async (invitationId: string) => {
    setResendNotice('')
    const result = await resendInvitations([invitationId])
    setResendNotice(
      result.failed === 0 ? 'Invitation resent.' : 'Could not resend this invitation. Please try again.'
    )
  }

  const handleApproveAffiliation = async (id: string) => {
    setRespondError('')
    const rate = parseFloat(rateByRequestId[id] ?? '')
    if (!Number.isFinite(rate) || rate <= 0) {
      setRespondError('Enter a valid hourly rate (must be greater than 0) before approving.')
      return
    }
    const result = await respondToAffiliation(id, true, rate)
    if (!result.ok) {
      setRespondError(AFFILIATION_RESPOND_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not approve this request. Please try again.')
    } else {
      // Approval creates a new active school_memberships row, but useSchoolAffiliationRequests
      // only refetches contractor_affiliation_requests — it has no reason to know about the
      // separate useInstructors() roster. Without this, the approved person vanishes from the
      // Pending row (correctly) but never appears as an active row until the page is reloaded.
      refresh()
    }
  }

  const handleRejectAffiliation = async (id: string) => {
    setRespondError('')
    const result = await respondToAffiliation(id, false, null)
    if (!result.ok) {
      setRespondError(AFFILIATION_RESPOND_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not reject this request. Please try again.')
    }
  }

  // Unified roster: active members + pending employee invitations + pending contractor
  // affiliation requests, so the two filter facets (role category, status) work the same
  // way across all three sources instead of three separately-filtered panels.
  const rows: RosterRow[] = useMemo(() => {
    const activeRows: RosterRow[] = instructors.map(inst => ({
      key: `active:${inst.membershipId}`,
      kind: 'active',
      fullName: inst.fullName,
      email: inst.email,
      jobTitle: inst.jobTitle,
      // A contractor is always an instructor by the product's own rule (migration 078 —
      // only instructors can self-request contractor affiliation), so it's filterable
      // under the Instructor role category even though role_category is never stored for
      // a contractor row.
      roleCategory: inst.relationshipType === 'contractor' ? 'instructor' : inst.roleCategory,
      status: inst.relationshipType === 'contractor' ? 'contractor' : inst.employmentType,
      hourlyRate: inst.hourlyRate,
      phone: inst.phone,
      vehicle: [inst.vehicleMake, inst.vehicleModel].filter(Boolean).join(' ') || null,
      vehiclePlate: inst.vehiclePlate,
      dateLabel: `Joined ${formatDate(inst.joinedAt)}`,
      dateIso: inst.joinedAt,
      membershipId: inst.membershipId,
    }))

    const pendingInviteRows: RosterRow[] = invitations
      .filter(inv => effectiveInviteStatus(inv.status, inv.expiresAt) === 'pending')
      .map(inv => ({
        key: `invite:${inv.id}`,
        kind: 'pending_invite',
        fullName: null,
        email: inv.invitedEmail,
        jobTitle: inv.jobTitle,
        roleCategory: inv.roleCategory,
        status: 'pending',
        hourlyRate: null,
        phone: null,
        vehicle: null,
        vehiclePlate: null,
        dateLabel: `Sent ${formatDate(inv.createdAt)} · Expires ${formatDate(inv.expiresAt)}`,
        dateIso: inv.createdAt,
        invitationId: inv.id,
      }))

    const pendingAffiliationRows: RosterRow[] = affiliationRequests
      .filter(req => req.status === 'pending')
      .map(req => ({
        key: `affil:${req.id}`,
        kind: 'pending_affiliation',
        fullName: req.instructorName,
        email: req.instructorEmail,
        jobTitle: 'Instructor',
        roleCategory: 'instructor',
        status: 'pending',
        hourlyRate: req.hourlyRate,
        phone: null,
        vehicle: null,
        vehiclePlate: null,
        dateLabel: `Requested ${formatDate(req.requestedAt)}`,
        dateIso: req.requestedAt,
        affiliationId: req.id,
      }))

    return [...activeRows, ...pendingInviteRows, ...pendingAffiliationRows]
      .sort((a, b) => b.dateIso.localeCompare(a.dateIso))
  }, [instructors, invitations, affiliationRequests])

  const visibleRows = rows.filter(r =>
    (roleFilter.size === 0 || (r.roleCategory !== null && roleFilter.has(r.roleCategory))) &&
    (statusFilter.size === 0 || (r.status !== null && statusFilter.has(r.status)))
  )

  const selectablePendingInviteIds = visibleRows.filter(r => r.kind === 'pending_invite').map(r => r.invitationId!)
  const allSelectableChecked = selectablePendingInviteIds.length > 0 && selectablePendingInviteIds.every(id => selected.has(id))

  const toggleRoleFilter = (key: RosterRoleCategory) => setRoleFilter(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const toggleStatusFilter = (key: RosterStatus) => setStatusFilter(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const toggleSelectAll = () => setSelected(
    allSelectableChecked ? new Set() : new Set(selectablePendingInviteIds)
  )
  const toggleSelectRow = (id: string) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const anyLoading = loading || invitesLoading || affiliationLoading
  const anyError = error || invitesError || affiliationError

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 6 }}>
        <div>
          <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
            Employees
          </div>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)' }}>
            Everyone who works at your school — active, and pending invitations/requests
          </div>
        </div>
        <button onClick={() => setWizardOpen(true)} className='btn-teal' style={{ width: 'auto', padding: '11px 20px', flexShrink: 0 }}>
          + Onboard employee
        </button>
      </div>

      <SchoolSwitcher />

      {formNotice && (
        <div style={{
          marginTop: 14, marginBottom: 6, padding: '12px 16px', borderRadius: 10,
          background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)',
          font: '400 13px Inter, sans-serif', color: '#1B2A4A',
        }}>
          {formNotice}
        </div>
      )}

      {wizardOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(27,42,74,0.5)', zIndex: 300,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 20px', overflowY: 'auto',
          }}
          onClick={() => setWizardOpen(false)}
        >
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 720 }}>
            <EmployeeOnboardingWizard
              schoolId={selectedSchoolId}
              hasSchool={schools.length > 0}
              schoolsLoading={schoolsLoading}
              onSent={handleWizardSent}
              onCancel={() => setWizardOpen(false)}
            />
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <MultiSelectDropdown
          label='Role category'
          options={ROLE_CATEGORY_OPTIONS}
          selected={roleFilter}
          onToggle={toggleRoleFilter}
          onClear={() => setRoleFilter(new Set())}
        />
        <MultiSelectDropdown
          label='Status'
          options={STATUS_OPTIONS}
          selected={statusFilter}
          onToggle={toggleStatusFilter}
          onClear={() => setStatusFilter(new Set())}
        />
        {(roleFilter.size > 0 || statusFilter.size > 0) && (
          <button
            onClick={() => { setRoleFilter(new Set()); setStatusFilter(new Set()) }}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
        {selectablePendingInviteIds.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', cursor: 'pointer' }}>
            <input type='checkbox' checked={allSelectableChecked} onChange={toggleSelectAll} />
            Select all pending invites
          </label>
        )}
        <button
          onClick={handleResendSelected}
          disabled={selected.size === 0 || resending}
          className='btn-teal'
          style={{
            width: 'auto', padding: '8px 16px', font: '600 12.5px Inter, sans-serif',
            opacity: selected.size === 0 ? 0.4 : 1,
            cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {resending ? 'Resending…' : `Resend invite${selected.size > 1 ? ` (${selected.size})` : ''}`}
        </button>
      </div>

      {revokeError && <div className='banner-error' style={{ marginBottom: 14 }}>{revokeError}</div>}
      {respondError && <div className='banner-error' style={{ marginBottom: 14 }}>{respondError}</div>}
      {resendNotice && (
        <div style={{
          marginBottom: 14, padding: '10px 16px', borderRadius: 10,
          background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)',
          font: '400 13px Inter, sans-serif', color: '#1B2A4A',
        }}>
          {resendNotice}
        </div>
      )}
      {anyError && !anyLoading && (
        <div className='banner-error' style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <span>{anyError}</span>
          <button
            onClick={() => { refresh(); refreshInvites(); refreshAffiliationRequests() }}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', flexShrink: 0 }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Roster */}
      {anyLoading && (
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>
          Loading…
        </div>
      )}

      {!anyLoading && !anyError && visibleRows.length === 0 && (
        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 14, padding: '36px 28px', textAlign: 'center',
        }}>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>
            {rows.length === 0 ? 'No employees yet' : 'No matches for these filters'}
          </div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            {rows.length === 0
              ? 'Employees will appear here once invited, and after they accept.'
              : 'Try clearing a filter above.'}
          </div>
        </div>
      )}

      {!anyLoading && !anyError && visibleRows.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          <div className='dt-head' style={{ gridTemplateColumns: '28px minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,0.9fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,0.9fr) minmax(0,1.5fr)' }}>
            <span />
            <span>Name</span>
            <span>Email</span>
            <span>Phone</span>
            <span>Type</span>
            <span>Car</span>
            <span>Reg no.</span>
            <span>Date</span>
            <span>Actions</span>
          </div>

          {visibleRows.map((row, i) => {
            const isLast = i === visibleRows.length - 1
            const s = row.status ? STATUS_STYLE[row.status] : null
            const clickable = row.kind === 'active'
            return (
              <div key={row.key}>
                <div
                  className='dt-row-desk'
                  onClick={clickable ? () => navigate(`/app/school/instructors/${row.membershipId}`) : undefined}
                  style={{
                    gridTemplateColumns: '28px minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,0.9fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,0.9fr) minmax(0,1.5fr)',
                    borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                >
                  <div onClick={e => e.stopPropagation()}>
                    {row.kind === 'pending_invite' && (
                      <input
                        type='checkbox'
                        checked={selected.has(row.invitationId!)}
                        onChange={() => toggleSelectRow(row.invitationId!)}
                      />
                    )}
                  </div>
                  <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                    {row.fullName ?? '—'}
                  </div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.email ?? '—'}
                  </div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                    {row.phone ?? '—'}
                  </div>
                  <div>
                    {s && (
                      <span style={{
                        padding: '4px 10px', borderRadius: 20, background: s.bg,
                        font: '600 11.5px Inter, sans-serif', color: s.color,
                      }}>
                        {s.label}
                      </span>
                    )}
                    {row.hourlyRate != null && (
                      <span style={{ marginLeft: 8, font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
                        ${row.hourlyRate.toFixed(2)}/hr
                      </span>
                    )}
                  </div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                    {row.vehicle ?? '—'}
                  </div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                    {row.vehiclePlate ?? '—'}
                  </div>
                  <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
                    {row.dateLabel}
                  </div>
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}
                  >
                    {row.kind === 'pending_invite' && (
                      <button
                        onClick={() => handleResendOne(row.invitationId!)}
                        disabled={resending}
                        style={{
                          padding: '4px 10px', background: 'transparent',
                          border: '1px solid rgba(27,42,74,0.15)', borderRadius: 7,
                          font: '500 11.5px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        Resend
                      </button>
                    )}
                    {row.kind === 'pending_invite' && (
                      <button
                        onClick={() => handleRevoke(row.invitationId!)}
                        disabled={revokingId === row.invitationId}
                        style={{
                          padding: '4px 10px', background: 'transparent',
                          border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7,
                          font: '500 11.5px Inter, sans-serif', color: '#C0522A', cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        {revokingId === row.invitationId ? '…' : 'Revoke'}
                      </button>
                    )}

                    {row.kind === 'pending_affiliation' && (() => {
                      const rateValue = parseFloat(rateByRequestId[row.affiliationId!] ?? '')
                      const rateValid = Number.isFinite(rateValue) && rateValue > 0
                      return (
                        <>
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <span style={{
                              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                              font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', pointerEvents: 'none',
                            }}>$</span>
                            <input
                              type='number'
                              min='0.01'
                              step='0.01'
                              placeholder='Rate'
                              value={rateByRequestId[row.affiliationId!] ?? ''}
                              onChange={e => setRateByRequestId(prev => ({ ...prev, [row.affiliationId!]: e.target.value }))}
                              className='field-input'
                              style={{ width: 70, padding: '4px 8px 4px 18px', font: '400 12px Inter, sans-serif' }}
                              disabled={respondingId === row.affiliationId}
                            />
                            <span style={{
                              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                              font: '400 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', pointerEvents: 'none',
                            }}>/hr</span>
                          </div>
                          <button
                            onClick={() => handleApproveAffiliation(row.affiliationId!)}
                            disabled={respondingId === row.affiliationId || !rateValid}
                            title={rateValid ? undefined : 'Enter an hourly rate before approving'}
                            className='btn-teal'
                            style={{
                              width: 'auto', padding: '4px 12px', font: '600 11.5px Inter, sans-serif', flexShrink: 0,
                              opacity: rateValid ? 1 : 0.4, cursor: rateValid ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {respondingId === row.affiliationId ? '…' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleRejectAffiliation(row.affiliationId!)}
                            disabled={respondingId === row.affiliationId}
                            style={{
                              padding: '4px 10px', background: 'transparent',
                              border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7,
                              font: '500 11.5px Inter, sans-serif', color: '#C0522A', cursor: 'pointer', flexShrink: 0,
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )
                    })()}
                  </div>
                </div>

                {/* Mobile row */}
                <div
                  className='dt-row-mob'
                  onClick={clickable ? () => navigate(`/app/school/instructors/${row.membershipId}`) : undefined}
                  style={{ borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)', cursor: clickable ? 'pointer' : 'default' }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {row.kind === 'pending_invite' && (
                        <input
                          type='checkbox'
                          onClick={e => e.stopPropagation()}
                          checked={selected.has(row.invitationId!)}
                          onChange={() => toggleSelectRow(row.invitationId!)}
                        />
                      )}
                      <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                        {row.fullName ?? row.email ?? '—'}
                      </div>
                    </div>
                    {row.fullName && (
                      <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                        {row.email}
                      </div>
                    )}
                    {(row.phone || row.vehicle || row.vehiclePlate) && (
                      <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                        {[row.phone, row.vehicle, row.vehiclePlate].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {s && (
                        <span style={{
                          padding: '3px 9px', borderRadius: 20, background: s.bg,
                          font: '600 11px Inter, sans-serif', color: s.color,
                        }}>
                          {s.label}
                        </span>
                      )}
                      {row.hourlyRate != null && (
                        <span style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>
                          ${row.hourlyRate.toFixed(2)}/hr
                        </span>
                      )}
                    </div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 4 }}>
                      {row.dateLabel}
                    </div>
                    {row.kind === 'pending_invite' && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleResendOne(row.invitationId!) }}
                          disabled={resending}
                          style={{
                            padding: '5px 12px', background: 'transparent',
                            border: '1px solid rgba(27,42,74,0.15)', borderRadius: 7,
                            font: '500 12px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer',
                          }}
                        >
                          Resend
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleRevoke(row.invitationId!) }}
                          disabled={revokingId === row.invitationId}
                          style={{
                            padding: '5px 12px', background: 'transparent',
                            border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7,
                            font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: 'pointer',
                          }}
                        >
                          {revokingId === row.invitationId ? '…' : 'Revoke'}
                        </button>
                      </div>
                    )}
                    {row.kind === 'pending_affiliation' && (() => {
                      const rateValue = parseFloat(rateByRequestId[row.affiliationId!] ?? '')
                      const rateValid = Number.isFinite(rateValue) && rateValue > 0
                      return (
                        <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <input
                            type='number'
                            min='0.01'
                            step='0.01'
                            placeholder='Rate/hr'
                            value={rateByRequestId[row.affiliationId!] ?? ''}
                            onChange={e => setRateByRequestId(prev => ({ ...prev, [row.affiliationId!]: e.target.value }))}
                            className='field-input'
                            style={{ width: 90, padding: '6px 10px' }}
                            disabled={respondingId === row.affiliationId}
                          />
                          <button
                            onClick={() => handleApproveAffiliation(row.affiliationId!)}
                            disabled={respondingId === row.affiliationId || !rateValid}
                            title={rateValid ? undefined : 'Enter an hourly rate before approving'}
                            className='btn-teal'
                            style={{
                              width: 'auto', padding: '6px 14px', font: '600 12px Inter, sans-serif',
                              opacity: rateValid ? 1 : 0.4, cursor: rateValid ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {respondingId === row.affiliationId ? '…' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleRejectAffiliation(row.affiliationId!)}
                            disabled={respondingId === row.affiliationId}
                            style={{
                              padding: '6px 12px', background: 'transparent',
                              border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7,
                              font: '500 12px Inter, sans-serif', color: '#C0522A', cursor: 'pointer',
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!anyLoading && visibleRows.length > 0 && (
        <div style={{ marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', textAlign: 'right' }}>
          {visibleRows.length} record{visibleRows.length !== 1 ? 's' : ''}
        </div>
      )}
      </div>
    </div>
  )
}
